import { ReducerCreators, PayloadAction } from '@reduxjs/toolkit';

import { ChannelsState } from './types';
import FetchChannels from '@/connection/channels/fetch-channels';
import { RootState } from '@/store/main';
import { UpdateChannel } from '@/connection/channel/update-channel';
import { Channel } from '@/types/channel.types';
import { eCDNConfiguration } from '@/types/ecdn.types';
import { serializeError } from '@/store/utils/serialize-error';

export const reducers = (create: ReducerCreators<ChannelsState>) => ({
  loadChannels: create.asyncThunk(
    async (data: { useCache?: boolean } | undefined, { rejectWithValue, getState }) => {
      try {
        const { useCache } = data || {};
        if (useCache) {
          const state = getState() as RootState;
          const channels = state?.channels?.channels as Channel[] | null;
          if (channels) {
            return channels;
          }
        }
        const results = await FetchChannels();
        if (!results) {
          return rejectWithValue('No channels found');
        }
        if ('error' in results) {
          return rejectWithValue(results.error);
        }

        return results;
      } catch (error) {
        return rejectWithValue(serializeError(error));
      }
    },
    {
      fulfilled: (state, action) => {
        if (action.payload) {
          state.channels = action.payload;
        }

        state.loadingChannels = false;
      },
      pending: (state) => {
        state.loadingChannels = true;
      },
      rejected: (state, action) => {
        state.loadingChannels = false;
        console.error(action?.error?.message || 'Error loading channels');
      },
    }
  ),
  updateChannel: create.asyncThunk(
    async ({ channel }: { channel: Channel }, { rejectWithValue }) => {
      try {
        const updatedChannel = await UpdateChannel(channel);
        return updatedChannel;
      } catch (error) {
        return rejectWithValue(serializeError(error));
      }
    },
    {
      fulfilled: (state, action) => {
        if (state.channels) {
          state.channels = state.channels.map((channel) => {
            return channel.channel === action.payload?.channel ? action.payload : channel;
          });
        }
        state.updateChannelError = false;
      },
      rejected: (state) => {
        state.updateChannelError = true;
      },
    }
  ),
  updateECDNConfiguration: create.reducer(
    (state, action: PayloadAction<{ channel: number; ecdn_configuration: eCDNConfiguration }>) => {
      if (state.channels) {
        state.channels = state.channels.map((channel) => {
          return channel.channel === action.payload?.channel
            ? {
                ...channel,
                ecdn_configuration: { ...channel.ecdn_configuration, ...action.payload.ecdn_configuration },
              }
            : channel;
        });
      }
    }
  ),
});
